import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, Stack, TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, IconButton, Button } from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 21 }, (_, i) => currentYear + i);

const columns = [
  { key: 'employeeName', width: 220 },
  { key: 'employeeEmail', width: 240 },
  { key: 'leaveTypeCode', width: 120 },
  { key: 'leaveTypeName', width: 200 },
  { key: 'entitledDays', width: 120, align: 'right' },
  { key: 'usedDays', width: 120, align: 'right' },
  { key: 'carriedOverDays', width: 140, align: 'right' },
  { key: 'reservedDays', width: 120, align: 'right' },
  { key: 'availableNow', width: 150, align: 'right' },
  { key: 'actions', width: 120, align: 'right' },
];

export default function LeaveBalanceManagement(){
  const { t } = useTranslation();
  const [year, setYear] = useState(currentYear);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeCode, setTypeCode] = useState('AL');
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const rowKey = (r) => `${r.employeeId}-${r.leaveTypeId}`;
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const data = await api.get(`/leave-balances/admin/list-all?year=${year}&page=${page+1}&pageSize=${pageSize}&typeCode=${encodeURIComponent(typeCode)}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(typeof e?.response?.data === 'string' ? e.response.data : (e.message || 'Error'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year, page, pageSize, typeCode]);

  // Load leave types for dropdown; default to 'AL' if present, else 'ALL'
  useEffect(() => {
    (async () => {
      try {
        const tps = await api.get('/leave-types');
        const arr = Array.isArray(tps) ? tps : [];
        setTypes(arr);
        const hasAL = arr.some(x => String(x.code||x.Code).toUpperCase() === 'AL');
        if (!hasAL && typeCode === 'AL') setTypeCode('ALL');
      } catch { /* ignore */ }
    })();
  }, []);

  const onEdit = (row) => { setEditKey(rowKey(row)); setEditDraft({ ...row }); };
  const onCancel = () => { setEditKey(null); setEditDraft({}); };
  const onChangeDraft = (key, val) => setEditDraft(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    try {
      const payload = {
        employeeId: editDraft.employeeId,
        leaveTypeId: editDraft.leaveTypeId,
        year,
        entitledDays: Number(editDraft.entitledDays) || 0,
        carriedOverDays: Number(editDraft.carriedOverDays) || 0,
        usedDays: Number(editDraft.usedDays) || 0,
      };
      await api.post('/leave-balances/admin/upsert', payload);
      // Optimistic update of the edited row without full reload
      setRows(prev => prev.map(r => {
        if (r.employeeId === payload.employeeId && r.leaveTypeId === payload.leaveTypeId) {
          const reserved = Number(r.reservedDays) || 0;
          const availableNow = payload.entitledDays + payload.carriedOverDays - payload.usedDays - reserved;
          return { ...r, entitledDays: payload.entitledDays, carriedOverDays: payload.carriedOverDays, usedDays: payload.usedDays, availableNow, year: payload.year };
        }
        return r;
      }));
      setEditKey(null); setEditDraft({});
    } catch (e) {
      setError(typeof e?.response?.data === 'string' ? e.response.data : (e.message || 'Error'));
    }
  };

  const headers = useMemo(() => ({
    employeeName: t('admin.leaveBalances.columns.employeeName', 'Employee'),
    employeeEmail: t('admin.leaveBalances.columns.employeeEmail', 'Email'),
    leaveTypeCode: t('admin.leaveBalances.columns.leaveTypeCode', 'Type Code'),
    leaveTypeName: t('admin.leaveBalances.columns.leaveTypeName', 'Type Name'),
    entitledDays: t('admin.leaveBalances.columns.entitledDays', 'Entitled'),
    usedDays: t('admin.leaveBalances.columns.usedDays', 'Used'),
    carriedOverDays: t('admin.leaveBalances.columns.carriedOverDays', 'Carried Over'),
    reservedDays: t('admin.leaveBalances.columns.reservedDays', 'Reserved'),
    availableNow: t('admin.leaveBalances.columns.availableNow', 'Available'),
    actions: t('admin.leaveBalances.columns.actions', 'Actions'),
  }), [t]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">{t('admin.leaveBalances.title', 'Leave Balances')}</Typography>
        <Box sx={{ flex: 1 }} />
        <TextField select size="small" label={t('admin.leaveBalances.year', 'Year')} value={year} onChange={e=>{setPage(0); setYear(Number(e.target.value));}} sx={{ minWidth: 140 }}>
          {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label={t('admin.leaveBalances.typeCode', 'Type Code')} value={typeCode} onChange={e=>{ setPage(0); setTypeCode(e.target.value); }} sx={{ minWidth: 180 }}>
          <MenuItem value="ALL">{t('common.selectAll', 'Select All')}</MenuItem>
          {types.map(lt => {
            const code = String(lt.code||lt.Code||'').toUpperCase();
            return <MenuItem key={lt.id||lt.Id||code} value={code}>{code}</MenuItem>;
          })}
        </TextField>
        <IconButton aria-label={t('admin.leaveBalances.refresh', 'Refresh')} onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Stack>

      <Paper sx={{ p: 0, bgcolor: 'background.paper' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map(c => (
                <TableCell key={c.key} align={c.align} sx={{ width: c.width, color: 'text.secondary' }}>{headers[c.key]}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => {
              const isEdit = editKey === rowKey(row);
              return (
                <TableRow key={rowKey(row)} hover>
                  <TableCell>{row.employeeName || row.employeeEmail}</TableCell>
                  <TableCell>{row.employeeEmail}</TableCell>
                  <TableCell>{row.leaveTypeCode}</TableCell>
                  <TableCell>{row.leaveTypeName}</TableCell>
                  <TableCell align="right">
                    {isEdit ? (
                      <TextField inputProps={{ inputMode:'numeric', pattern:'[0-9]*' }} value={editDraft.entitledDays ?? ''} onChange={e=>onChangeDraft('entitledDays', e.target.value)} size="small" sx={{ maxWidth: 100 }} />
                    ) : row.entitledDays}
                  </TableCell>
                  <TableCell align="right">
                    {isEdit ? (
                      <TextField inputProps={{ inputMode:'numeric', pattern:'[0-9]*' }} value={editDraft.usedDays ?? ''} onChange={e=>onChangeDraft('usedDays', e.target.value)} size="small" sx={{ maxWidth: 100 }} />
                    ) : row.usedDays}
                  </TableCell>
                  <TableCell align="right">
                    {isEdit ? (
                      <TextField inputProps={{ inputMode:'numeric', pattern:'[0-9]*' }} value={editDraft.carriedOverDays ?? ''} onChange={e=>onChangeDraft('carriedOverDays', e.target.value)} size="small" sx={{ maxWidth: 100 }} />
                    ) : row.carriedOverDays}
                  </TableCell>
                  <TableCell align="right">{row.reservedDays}</TableCell>
                  <TableCell align="right">{(() => {
                    const e = isEdit ? Number(editDraft.entitledDays||0) : Number(row.entitledDays||0);
                    const u = isEdit ? Number(editDraft.usedDays||0) : Number(row.usedDays||0);
                    const c = isEdit ? Number(editDraft.carriedOverDays||0) : Number(row.carriedOverDays||0);
                    const rsv = Number(row.reservedDays||0);
                    return e + c - u - rsv;
                  })()}</TableCell>
                  <TableCell align="right">
                    {isEdit ? (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton aria-label={t('common.save', 'Save')} color="primary" onClick={save}><SaveIcon /></IconButton>
                        <IconButton aria-label={t('common.cancel', 'Cancel')} onClick={onCancel}><CloseIcon /></IconButton>
                      </Stack>
                    ) : (
                      <IconButton aria-label={t('admin.leaveBalances.edit', 'Edit')} onClick={()=>onEdit(row)}><EditIcon /></IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage)=>setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e)=>{ setPage(0); setPageSize(parseInt(e.target.value, 10)); }}
          rowsPerPageOptions={[10,30,50,100]}
        />
      </Paper>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>{error}</Typography>
      )}
    </Box>
  );
}

